import { Flex, FormControl, FormLabel, Input, Text } from '@chakra-ui/react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { ResponsibleMedicalRecord } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecord'
import { ResponsibleMedicalRecordReview } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordReview'
import { ResponsibleMedicalRecordView } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/ResponsibleMedicalRecordView'
import { ReviewResponsibleMedicalRecordForm } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/domain/models/UpdateMedicalRecordDTO'

import { MedicalRecordReviewType } from '../../../../constants/MedicalRecordReviewType'
import { ReviewDataCheckboxArrayValue } from '../components/ReviewDataCheckbox'
import { useReviewFormRepeatedMultipleArrayValues } from '../components/ReviewFormRepeatedValue'
import ReviewStepWrapper from '../components/ReviewStepWrapper'

export function ComorbiditiesReviewStep({
  data,
  mode,
  isLoading
}: { isLoading?: boolean } & (
  | {
      data: ResponsibleMedicalRecordView
      mode: typeof MedicalRecordReviewType.enum.VIEW
    }
  | {
      data: ResponsibleMedicalRecordReview
      mode: typeof MedicalRecordReviewType.enum.REVIEW
    }
)) {
  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()

  useReviewFormRepeatedMultipleArrayValues({
    disabled: mode === MedicalRecordReviewType.enum.VIEW,
    form,
    formProp: 'comorbidities',
    serverValue: data.registerPatient.comorbidities,
    responsibleValue: data.sendResponsible.comorbidities,
    comparison: (a, b) => (!a ? false : !b ? false : a.name === b.name)
  })

  return (
    <Flex justifyContent={'space-between'} gap={'6'} overflowX={'clip'}>
      <FoodReviewPart
        sourceData={data.sendResponsible}
        comparisonData={
          mode === 'REVIEW' ? data.registerPatient : form.getValues()
        }
        mode={mode}
        isLoading={isLoading}
      />
      <FoodReviewPart
        sourceData={data.registerPatient}
        comparisonData={
          mode === 'REVIEW' ? data.sendResponsible : form.getValues()
        }
        mode={mode}
        isLoading={isLoading}
      />
    </Flex>
  )
}

function FoodReviewPart({
  sourceData,
  comparisonData,
  mode,
  isLoading
}: {
  sourceData: ResponsibleMedicalRecord
  comparisonData: ReviewResponsibleMedicalRecordForm | ResponsibleMedicalRecord
  mode: MedicalRecordReviewType
  isLoading?: boolean
}) {
  const form = useFormContext<ReviewResponsibleMedicalRecordForm>()
  const [comorbidities] = form.watch(['comorbidities'])
  const comorbiditiesFieldArray = useFieldArray({
    control: form.control,
    name: 'comorbidities'
  })

  return (
    <ReviewStepWrapper>
      {sourceData.comorbidities.length === 0 ? (
        <Flex>
          <Text>Sem opções disponíveis</Text>
        </Flex>
      ) : (
        sourceData.comorbidities.map((comorbidity) => {
          return (
            <Flex
              key={comorbidity.id}
              alignItems={'flex-start'}
              flexDir={'column'}
            >
              <Flex gap={'4'} w={'full'}>
                <FormControl>
                  <FormLabel>Descrição</FormLabel>
                  <Input
                    isDisabled
                    value={comorbidity.name}
                    border={'solid 1px'}
                    borderColor={
                      comorbidity.name ? 'custom.gray.400' : 'custom.gray.600'
                    }
                  />
                </FormControl>
              </Flex>
              <ReviewDataCheckboxArrayValue
                disabled={
                  mode === MedicalRecordReviewType.enum.VIEW || isLoading
                }
                checkboxProps={{
                  mt: '4'
                }}
                parallelValues={comparisonData.comorbidities}
                currentValue={comorbidity}
                formValue={comorbidities}
                set={(v) => comorbiditiesFieldArray.append(v)}
                reset={() =>
                  comorbiditiesFieldArray.remove(
                    comorbiditiesFieldArray.fields.findIndex(
                      (item) => item.name === comorbidity.name
                    ) ?? -1
                  )
                }
                comparison={(a, b) => a.name === b.name}
              />
            </Flex>
          )
        })
      )}
    </ReviewStepWrapper>
  )
}
